// Goals by Time Band — D3 SVG diverging bar chart (Phase 3).
//
// React owns the container + the <svg> skeleton (just the baseline line); D3
// owns the per-band bar groups via a keyed enter/update/exit join in a useEffect
// (a port of the legacy draw), so bars animate to new heights on a control
// change. The skeleton persists across control changes (like Set Piece), so
// there's no fixture key — the first draw for the mount snaps, later draws
// animate. ResizeObserver redraws on resize. Emits the same class names + DOM as
// the legacy renderer (verified by the visual A/B).

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { easeCubicInOut } from "d3-ease";
import { selectGtbBands, type GtbBand } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

const DURATION = 400;

export interface GoalsByTimeBandProps {
  fixture: string;
  team?: string | null;
  timeline?: string | null;
}

function draw(
  svgEl: SVGSVGElement,
  bands: GtbBand[],
  useTransition: boolean
): void {
  const W = svgEl.clientWidth;
  const H = svgEl.clientHeight;
  if (W < 20 || H < 20) return; // not laid out yet — the ResizeObserver retries

  const maxScored = bands.length ? Math.max(...bands.map((d) => d.s)) : 0;
  const maxConceded = bands.length ? Math.max(...bands.map((d) => d.c)) : 0;
  const totalRange = maxScored + maxConceded || 1;

  const mT = 14,
    mB = 26,
    mL = 4,
    mR = 4;
  const cW = W - mL - mR;
  const cH = H - mT - mB;
  const pxPerUnit = cH / totalRange;
  const mid = mT + maxScored * pxPerUnit;

  const n = bands.length;
  const bw = (cW * 0.55) / n;
  const gap = n > 1 ? (cW - bw * n) / (n - 1) : 0;
  const barX = (i: number) => mL + i * (bw + gap);
  const scaleH = (v: number) => Math.max(0, v * pxPerUnit);
  const dur = useTransition ? DURATION : 0;
  const ease = easeCubicInOut;
  const svg = select(svgEl);

  svg
    .select(".gtb-baseline")
    .attr("x1", mL)
    .attr("y1", mid)
    .attr("x2", W - mR)
    .attr("y2", mid)
    .raise();

  const groups = svg
    .selectAll<SVGGElement, GtbBand>(".gtb-band")
    .data(bands, (d) => d.l);

  const entering = groups.enter().append("g").attr("class", "gtb-band");
  entering
    .append("rect")
    .attr("class", "gtb-s-bar bento-fill")
    .attr("fill", "#2ecc71")
    .attr("rx", 4)
    .attr("y", mid)
    .attr("height", 0);
  entering
    .append("rect")
    .attr("class", "gtb-c-bar bento-fill")
    .attr("fill", "#e74c3c")
    .attr("rx", 4)
    .attr("y", mid)
    .attr("height", 0);
  entering
    .append("text")
    .attr("class", "gtb-s-val")
    .attr("text-anchor", "middle")
    .attr("fill", "#2ecc71")
    .style("font-size", "var(--t-data)")
    .style("font-weight", "700");
  entering
    .append("text")
    .attr("class", "gtb-c-val")
    .attr("text-anchor", "middle")
    .attr("fill", "#e74c3c")
    .style("font-size", "var(--t-data)")
    .style("font-weight", "700");
  entering
    .append("text")
    .attr("class", "gtb-label")
    .attr("text-anchor", "middle")
    .attr("fill", "#888")
    .style("font-size", "var(--t-label)");

  entering.merge(groups).each(function (this: SVGGElement, d, i) {
    const x = barX(i);
    const cx = x + bw / 2;
    const sh = scaleH(d.s);
    const ch = scaleH(d.c);
    const g = select(this);

    g.select(".gtb-s-bar")
      .attr("x", x)
      .attr("width", bw)
      .transition()
      .duration(dur)
      .ease(ease)
      .attr("y", mid - sh)
      .attr("height", sh);
    g.select(".gtb-c-bar")
      .attr("x", x)
      .attr("width", bw)
      .attr("y", mid)
      .transition()
      .duration(dur)
      .ease(ease)
      .attr("height", ch);
    g.select(".gtb-s-val")
      .attr("x", cx)
      .transition()
      .duration(dur)
      .ease(ease)
      .attr("y", mid - sh - 3)
      .text(d.s > 0 ? String(d.s) : "");
    g.select(".gtb-c-val")
      .attr("x", cx)
      .transition()
      .duration(dur)
      .ease(ease)
      .attr("y", mid + ch + 11)
      .text(d.c > 0 ? String(d.c) : "");
    g.select(".gtb-label")
      .attr("x", cx)
      .attr("y", H - 0)
      .text(d.l);
  });

  groups.exit().remove();
}

export default function GoalsByTimeBand({
  fixture,
  team,
  timeline,
}: GoalsByTimeBandProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const firstRef = useRef(true);

  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const chosen = team || (parts[0] ?? "").trim();
  // Data now from app-api (was the `getGoalsByTimeBandData` legacyData getter);
  // the D3 draw is unchanged. Controls reshape client-side, no refetch.
  const { data, isPending, isError, refetch } = useWidgetData(
    "goals-by-time-band",
    useFixtureKey(fixture)
  );
  const bands = selectGtbBands(data ?? {}, chosen, timeline);
  const sig = `${chosen}|${JSON.stringify(bands)}`;

  // Hooks run unconditionally (above the isPending/isError early returns); while
  // pending `bands` is empty so the draw is a no-op, then re-runs once loaded.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const useTransition = !firstRef.current;
    firstRef.current = false;
    draw(svgEl, bands, useTransition);

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => draw(svgEl, bands, false));
    ro.observe(svgEl);
    return () => ro.disconnect();
    // `sig` captures team + band data (the complete draw input); `bands` fresh.
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  return (
    <div className="data-wrapper top-align">
      <svg className="gtb-svg" ref={svgRef}>
        <line className="gtb-baseline" stroke="#222" strokeWidth="1.5" />
      </svg>
    </div>
  );
}
